<template>
  <v-container fluid>
    <section>
      <!-- Page Header -->
      <v-row class="mb-4" align="center">
        <v-col cols="12">
          <div class="d-flex align-center flex-wrap gap-3">

            <!-- Left: back + title + status chip -->
            <div class="d-flex align-center flex-wrap gap-2">
              <v-btn
                depressed
                outlined
                color="primary"
                class="text-capitalize"
                small
                @click="$router.push('/marketplace-orders')"
              >
                <v-icon left small>fal fa-arrow-left</v-icon>
                {{ $t("back") || "Back" }}
              </v-btn>
              <span class="text-h6 font-weight-bold">
                {{ $t("order_detail") || "Order Detail" }}
              </span>
              <span class="text-subtitle-1 grey--text">
                #{{ form.txn || form.code || "-" }}
              </span>
              <v-chip
                small
                :color="statusColor(form.status)"
                dark
                class="text-capitalize rounded-lg font-weight-medium"
              >
                {{ $t(form.status) || form.status }}
              </v-chip>
            </div>

            <!-- Right: action buttons -->
            <div class="d-flex flex-wrap gap-2" style="margin-left: auto;">
              <v-btn
                depressed
                outlined
                color="primary"
                class="text-capitalize rounded-lg"
                style="min-height: 36px"
                :loading="printing"
                @click="printReceipt"
              >
                <v-icon left small>fal fa-print</v-icon>
                {{ $t("print") || "Print" }}
              </v-btn>
              <v-btn
                depressed
                color="primary"
                class="text-capitalize rounded-lg"
                style="min-height: 36px"
                :disabled="!canConfirm || updating"
                @click="openConfirm('shipping')"
              >
                <v-icon left small>fal fa-check</v-icon>
                {{ $t("confirm") || "Confirm" }}
              </v-btn>
              <v-btn
                depressed
                color="green"
                dark
                class="text-capitalize rounded-lg"
                style="min-height: 36px"
                :disabled="!canComplete || updating"
                @click="openConfirm('completed')"
              >
                <v-icon left small>fal fa-box-check</v-icon>
                {{ $t("complete_order") || "Complete" }}
              </v-btn>
              <v-btn
                depressed
                color="red"
                dark
                class="text-capitalize rounded-lg"
                style="min-height: 36px"
                :disabled="!canCancel || updating"
                @click="openConfirm('cancelled')"
              >
                <v-icon left small>fal fa-times</v-icon>
                {{ $t("cancel") || "Cancel" }}
              </v-btn>
            </div>

          </div>
        </v-col>
      </v-row>

      <!-- Skeleton loader -->
      <template v-if="loading">
        <v-row>
          <v-col cols="12" md="8">
            <v-card rounded="lg" outlined class="mb-4">
              <v-card-text>
                <v-skeleton-loader type="article" />
              </v-card-text>
            </v-card>
            <v-card rounded="lg" outlined>
              <v-card-text>
                <v-skeleton-loader type="table" />
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card rounded="lg" outlined class="mb-4">
              <v-card-text>
                <v-skeleton-loader type="article" />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <template v-else>
        <v-row>
          <!-- Left Column -->
          <v-col cols="12" md="8">

            <!-- Order Info -->
            <v-card rounded="lg" outlined class="mb-4">
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-receipt</v-icon>
                {{ $t("order_info") || "Order Information" }}
              </v-card-title>
              <v-divider />
              <v-card-text class="pb-2">
                <v-row dense>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">{{ $t("order_id") || "Order ID" }}</div>
                    <div class="font-weight-medium">{{ form.txn || form.code || "-" }}</div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">{{ $t("status") || "Status" }}</div>
                    <v-chip
                      x-small
                      :color="statusColor(form.status)"
                      dark
                      class="text-capitalize rounded-lg"
                    >
                      {{ $t(form.status) || form.status || "-" }}
                    </v-chip>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">{{ $t("type") || "Type" }}</div>
                    <div class="font-weight-medium text-capitalize">
                      {{ form.type || "-" }}
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="text-caption grey--text">{{ $t("payment_method") || "Payment" }}</div>
                    <div class="font-weight-medium text-capitalize">
                      {{ (form.payment_method || "-").replace(/_/g, " ") }}
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3" class="mt-2">
                    <div class="text-caption grey--text">{{ $t("date") || "Date" }}</div>
                    <div class="font-weight-medium">{{ form.created_at | moment }}</div>
                  </v-col>
                  <v-col cols="6" sm="3" class="mt-2">
                    <div class="text-caption grey--text">{{ $t("total_qty") || "Total Qty" }}</div>
                    <div class="font-weight-medium">{{ form.total_qty || 0 }}</div>
                  </v-col>
                  <v-col cols="12" sm="6" class="mt-2" v-if="form.note">
                    <div class="text-caption grey--text">{{ $t("note") || "Note" }}</div>
                    <div>{{ form.note }}</div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Line Items -->
            <v-card rounded="lg" outlined class="mb-4">
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-box-open</v-icon>
                {{ $t("order_items") || "Order Items" }}
                <v-chip x-small class="ml-2" outlined color="primary">
                  {{ (form.history || []).length }}
                </v-chip>
              </v-card-title>
              <v-divider />
              <v-simple-table>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th style="width:52px"></th>
                      <th>{{ $t("product") || "Product" }}</th>
                      <th>{{ $t("category") || "Category" }}</th>
                      <th>{{ $t("vendor") || "Vendor" }}</th>
                      <th class="text-center">{{ $t("qty") || "Qty" }}</th>
                      <th class="text-right">{{ $t("price") || "Price" }}</th>
                      <th class="text-right">{{ $t("total") || "Total" }}</th>
                      <th style="width:48px"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(h, i) in form.history || []" :key="i">
                      <td class="py-2">
                        <v-avatar tile size="40" class="rounded-lg" color="grey lighten-3">
                          <app-img :img="h.product && h.product.img" />
                        </v-avatar>
                      </td>
                      <td>
                        <div class="font-weight-medium">
                          {{ h.product && h.product.name || "-" }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ h.product && h.product.code || "" }}
                        </div>
                      </td>
                      <td>
                        <span class="text-caption">
                          {{ h.product && h.product.category && (h.product.category.en_name || h.product.category.la_name) || "-" }}
                        </span>
                      </td>
                      <td>
                        <span class="text-caption">
                          {{ h.product && h.product.vendor && h.product.vendor.name || "-" }}
                        </span>
                      </td>
                      <td class="text-center">{{ h.qty }}</td>
                      <td class="text-right">{{ h.price_sale | NumberFormat }}</td>
                      <td class="text-right font-weight-medium">
                        {{ h.total_price | NumberFormat }}
                      </td>
                      <td class="text-center">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              x-small
                              fab
                              depressed
                              class="rounded-lg"
                              :disabled="!h.product_id"
                              @click="viewProduct(h.product_id)"
                            >
                              <v-icon x-small color="blue">fal fa-eye</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ $t("description") || "Detail" }}</span>
                        </v-tooltip>
                      </td>
                    </tr>
                    <tr v-if="!form.history || !form.history.length">
                      <td colspan="8" class="text-center grey--text py-4">
                        {{ $t("no_data") || "No items" }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>

            <!-- Status Timeline -->
            <v-card rounded="lg" outlined class="mb-4" v-if="form.status_events && form.status_events.length">
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-history</v-icon>
                {{ $t("status_history") || "Status History" }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <v-timeline dense align-top>
                  <v-timeline-item
                    v-for="(ev, i) in form.status_events"
                    :key="i"
                    :color="statusColor(ev.to_status)"
                    fill-dot
                  >
                    <template v-slot:icon>
                      <v-icon x-small color="white">{{ statusIcon(ev.to_status) }}</v-icon>
                    </template>
                    <div class="d-flex align-start justify-space-between flex-wrap gap-2">
                      <div>
                        <!-- Status transition -->
                        <div class="d-flex align-center flex-wrap gap-2 mb-1">
                          <v-chip
                            x-small
                            :color="statusColor(ev.to_status)"
                            dark
                            class="text-capitalize rounded-lg font-weight-medium"
                          >
                            {{ $t(ev.to_status) || ev.to_status }}
                          </v-chip>
                          <template v-if="ev.from_status">
                            <v-icon x-small color="grey lighten-1">fal fa-long-arrow-left</v-icon>
                            <v-chip
                              x-small
                              :color="statusColor(ev.from_status)"
                              outlined
                              class="text-capitalize rounded-lg"
                            >
                              {{ $t(ev.from_status) || ev.from_status }}
                            </v-chip>
                          </template>
                        </div>
                        <!-- Note -->
                        <div class="text-body-2 mb-1" v-if="ev.note">
                          {{ resolveNote(ev.note) }}
                        </div>
                        <!-- Actor -->
                        <div class="d-flex align-center gap-1" v-if="ev.actor">
                          <v-icon x-small color="grey">fal fa-user</v-icon>
                          <span class="text-caption grey--text">{{ ev.actor.first_name }}</span>
                        </div>
                      </div>
                      <!-- Date -->
                      <div class="text-caption grey--text text-no-wrap">
                        {{ ev.created_at | moment }}
                      </div>
                    </div>
                  </v-timeline-item>
                </v-timeline>
              </v-card-text>
            </v-card>

            <!-- Action Note -->
            <v-card rounded="lg" outlined>
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-sticky-note</v-icon>
                {{ $t("add_note") || "Add Note" }}
              </v-card-title>
              <v-divider />
              <v-card-text>
                <v-textarea
                  v-model="actionNote"
                  :placeholder="$t('note_placeholder') || 'Write a note for this order...'"
                  outlined
                  dense
                  rows="3"
                  hide-details
                />
              </v-card-text>
            </v-card>

          </v-col>

          <!-- Right Column -->
          <v-col cols="12" md="4">

            <!-- Customer Info -->
            <v-card rounded="lg" outlined class="mb-4">
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-user</v-icon>
                {{ $t("customer") || "Customer" }}
              </v-card-title>
              <v-divider />
              <v-card-text class="pb-2">
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("name") || "Name" }}</span>
                  <span class="font-weight-medium">
                    {{ form.buyer_user && form.buyer_user.first_name || "-" }}
                  </span>
                </div>
                <v-divider class="my-2" />
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("phone_no") || "Phone" }}</span>
                  <span>{{ form.buyer_user && form.buyer_user.phone_no || "-" }}</span>
                </div>
                <v-divider class="my-2" />
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("address") || "Address" }}</span>
                  <span>{{ form.buyer_user && form.buyer_user.address || "-" }}</span>
                </div>
              </v-card-text>
            </v-card>

            <!-- Delivery Address -->
            <v-card rounded="lg" outlined class="mb-4" v-if="form.address">
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-map-marker-alt</v-icon>
                {{ $t("delivery_address") || "Delivery Address" }}
              </v-card-title>
              <v-divider />
              <v-card-text class="pb-2">
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("name") || "Name" }}</span>
                  <span class="font-weight-medium">{{ form.address.name || "-" }}</span>
                </div>
                <v-divider class="my-2" />
                <div class="text-caption grey--text mb-1">{{ $t("noted") || "Detail" }}</div>
                <div class="text-body-2">{{ form.address.noted || "-" }}</div>
              </v-card-text>
            </v-card>

            <!-- Financial Summary -->
            <v-card rounded="lg" outlined>
              <v-card-title class="text-subtitle-1 font-weight-bold pb-2">
                <v-icon left small color="primary">fal fa-calculator</v-icon>
                {{ $t("summary") || "Summary" }}
              </v-card-title>
              <v-divider />
              <v-card-text class="pb-2">
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("subtotal") || "Subtotal" }}</span>
                  <span>{{ form.total_price_sale | NumberFormat }}</span>
                </div>
                <v-divider class="my-2" />
                <div class="info-row" v-if="form.total_discount > 0">
                  <span class="text-caption grey--text">{{ $t("discount") || "Discount" }}</span>
                  <span class="red--text">-{{ form.total_discount | NumberFormat }}</span>
                </div>
                <div class="info-row" v-if="form.tax_amount > 0">
                  <span class="text-caption grey--text">{{ $t("tax") || "Tax" }}</span>
                  <span>{{ form.tax_amount | NumberFormat }}</span>
                </div>
                <div class="info-row" v-if="form.delivery_fee > 0">
                  <span class="text-caption grey--text">{{ $t("delivery_fee") || "Delivery Fee" }}</span>
                  <span>{{ form.delivery_fee | NumberFormat }}</span>
                </div>
                <v-divider class="my-2" />
                <div class="info-row">
                  <span class="font-weight-bold">{{ $t("total_price") || "Total" }}</span>
                  <span class="font-weight-bold text-primary">
                    {{ form.total_price | NumberFormat }}
                  </span>
                </div>
                <v-divider class="my-2" />
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("total_paid") || "Paid" }}</span>
                  <span class="green--text font-weight-medium">
                    {{ form.total_paid | NumberFormat }}
                  </span>
                </div>
                <div class="info-row" v-if="form.total_overdue > 0">
                  <span class="text-caption grey--text">{{ $t("total_overdue") || "Overdue" }}</span>
                  <span class="red--text">{{ form.total_overdue | NumberFormat }}</span>
                </div>
                <v-divider class="my-2" />
                <div class="info-row">
                  <span class="text-caption grey--text">{{ $t("profit") || "Profit" }}</span>
                  <span class="font-weight-medium" :class="form.total_profit >= 0 ? 'green--text' : 'red--text'">
                    {{ form.total_profit | NumberFormat }}
                  </span>
                </div>
              </v-card-text>
            </v-card>

          </v-col>
        </v-row>
      </template>

      <!-- Confirm/Cancel Dialog -->
      <app-custom-dialog
        v-model="confirmOpen"
        :footer="false"
        :title="confirmDialogTitle"
        width="480"
      >
        <div slot="activator">
          <div class="text-body-2">
            <div class="mb-1">
              <span class="grey--text">{{ $t("order_id") || "Order" }}:</span>
              <b class="ml-1">{{ form.txn || form.code || "-" }}</b>
            </div>
            <div class="mb-3">
              <span class="grey--text">{{ $t("total_price") || "Total" }}:</span>
              <b class="ml-1">{{ form.total_price | NumberFormat }}</b>
            </div>
            <div class="text-caption">{{ confirmDialogDesc }}</div>
          </div>
          <div class="d-flex justify-end gap-2 mt-5">
            <v-btn text class="text-capitalize" :disabled="updating" @click="confirmOpen = false">
              {{ $t("not_now") || "Not now" }}
            </v-btn>
            <v-btn
              :color="confirmDialogColor"
              dark
              depressed
              class="text-capitalize"
              style="min-height: 44px"
              :loading="updating"
              @click="submitStatus"
            >
              <v-icon left small>{{ confirmDialogIcon }}</v-icon>
              {{ confirmDialogBtnText }}
            </v-btn>
          </div>
        </div>
      </app-custom-dialog>

      <!-- Product Detail Dialog -->
      <app-custom-dialog
        v-model="productDialog"
        :footer="false"
        title="description"
        width="750"
      >
        <div slot="activator">
          <div v-if="loadingProduct" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <AppDetail v-else-if="productForm && productForm.id" :item="productForm" />
        </div>
      </app-custom-dialog>

      <app-loading :overlay="pageLoading" />
    </section>

    <!-- Print container -->
    <app-bill v-show="print" :item="printForm" />
  </v-container>
</template>

<script>
import PubNub from "pubnub";
import { success, toast } from "@/controllers/alert";
import { pubnubConfig } from "@/utils/mock";
import AppBill from "@/components/print/bill";
import AppDetail from "@/components/products/details.vue";
import { myMixin } from "@/mixin/main";
import { mapMutations } from "vuex";

export default {
  name: "MarketplaceOrderDetail",
  mixins: [myMixin],
  components: { AppBill, AppDetail },
  data: () => ({
    pubnub: new PubNub(pubnubConfig()),
    _pubnubListener: null,
    _subscribedChannel: null,
    loading: true,
    pageLoading: false,
    form: {},
    actionNote: "",
    updating: false,
    confirmOpen: false,
    confirmAction: null,
    printing: false,
    print: false,
    printForm: {},
    productDialog: false,
    productForm: {},
    loadingProduct: false,
  }),
  computed: {
    confirmDialogTitle() {
      if (this.confirmAction === "shipping")  return "confirm_order";
      if (this.confirmAction === "completed") return "complete_order";
      return "cancel_order";
    },
    confirmDialogDesc() {
      if (this.confirmAction === "shipping")
        return this.$t("confirm_order_desc") || "This will mark the order as accepted and in shipping.";
      if (this.confirmAction === "completed")
        return this.$t("complete_order_desc") || "This will mark the order as delivered and completed.";
      return this.$t("cancel_order_desc") || "This will cancel the order for the customer.";
    },
    confirmDialogColor() {
      if (this.confirmAction === "shipping")  return "primary";
      if (this.confirmAction === "completed") return "green";
      return "red";
    },
    confirmDialogIcon() {
      if (this.confirmAction === "shipping")  return "fal fa-check";
      if (this.confirmAction === "completed") return "fal fa-box-check";
      return "fal fa-times";
    },
    confirmDialogBtnText() {
      if (this.confirmAction === "shipping")  return this.$t("confirm") || "Confirm";
      if (this.confirmAction === "completed") return this.$t("complete_order") || "Complete";
      return this.$t("cancel") || "Cancel";
    },
    canConfirm() {
      return String(this.form.status || "").toLowerCase() === "processing";
    },
    canComplete() {
      return String(this.form.status || "").toLowerCase() === "shipping";
    },
    canCancel() {
      const s = String(this.form.status || "").toLowerCase();
      return s === "processing" || s === "shipping";
    },
  },
  mounted() {
    this.fetchDetail();
    this._subscribeMerchantChannel();
  },
  beforeDestroy() {
    this._unsubscribeMerchantChannel();
  },
  methods: {
    ...mapMutations({ SET_OVERLAY: "index/SET_OVERLAY" }),
    _subscribeMerchantChannel() {
      if (this._pubnubListener) return;
      const storeCode = this.user && this.user.stores && this.user.stores.code;
      if (!storeCode) return;
      const channel = `${storeCode}_marketplace`;
      this._subscribedChannel = channel;
      this._pubnubListener = {
        message: (event) => {
          const msg = event && event.message;
          const ev = msg && msg.event;
          const orderId = this.form && this.form.id;
          if (!orderId) return;
          const isThisOrder = Number(msg.ticket_id) === Number(orderId);
          if (
            isThisOrder &&
            (
              ev === "marketplace_order_cancelled" ||
              ev === "marketplace_order_completed" ||
              ev === "marketplace_order_status_changed"
            )
          ) {
            toast(
              this,
              ev === "marketplace_order_cancelled"
                ? this.$t("order_cancelled") || "Order cancelled by customer"
                : this.$t("order_completed") || "Customer confirmed delivery",
              ev === "marketplace_order_cancelled" ? "info" : "success",
            );
            this.fetchDetail();
          }
        },
      };
      this.pubnub.addListener(this._pubnubListener);
      this.pubnub.subscribe({ channels: [channel] });
    },
    _unsubscribeMerchantChannel() {
      try {
        if (this._pubnubListener) this.pubnub.removeListener(this._pubnubListener);
        if (this._subscribedChannel) this.pubnub.unsubscribe({ channels: [this._subscribedChannel] });
        this._pubnubListener = null;
        this._subscribedChannel = null;
      } catch (_) {}
    },
    async fetchDetail() {
      try {
        this.loading = true;
        const id = this.$route.params.id;
        const { data } = await this.$http.get(`get_orders_dash_detail/${id}`);
        this.form = data && data.data || {};
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    resolveNote(note) {
      const map = {
        checkout:           "note_checkout",
        customer_cancel:    "note_customer_cancel",
        customer_received:  "note_customer_received",
      };
      const key = map[note];
      return key ? this.$t(key) : note;
    },
    statusColor(status) {
      const map = {
        processing: "orange",
        shipping: "blue",
        completed: "green",
        cancelled: "red",
        refunded: "grey",
      };
      return map[String(status || "").toLowerCase()] || "grey";
    },
    statusIcon(status) {
      const map = {
        processing: "fal fa-clock",
        shipping:   "fal fa-truck",
        completed:  "fal fa-check",
        cancelled:  "fal fa-times",
        refunded:   "fal fa-undo",
      };
      return map[String(status || "").toLowerCase()] || "fal fa-circle";
    },
    openConfirm(action) {
      this.confirmAction = action;
      this.confirmOpen = true;
    },
    async submitStatus() {
      if (!this.form.id || !this.confirmAction) return;
      try {
        this.updating = true;
        const { data } = await this.$http.post("update_status", {
          status: this.confirmAction,
          note: this.actionNote || "",
          order_id: this.form.id,
        });
        this.confirmOpen = false;
        success(this, data.message);
        await this.fetchDetail();
      } catch (e) {
        console.log(e);
      } finally {
        this.updating = false;
      }
    },
    async viewProduct(id) {
      if (!id) return;
      this.productForm = {};
      this.productDialog = true;
      this.loadingProduct = true;
      try {
        const { data } = await this.$http.get(`products/${id}`);
        if (data.status) {
          this.productForm = data.data;
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.loadingProduct = false;
      }
    },
    async printReceipt() {
      if (!this.form.id) return;
      try {
        this.printing = true;
        const { data } = await this.$http.get(`bill_detail/${this.form.id}`);
        if (data.status) {
          this.printForm = data.data;
          this.print = true;
          await this.$nextTick();
          setTimeout(() => {
            this.$htmlToPaper("printBill");
            this.print = false;
          }, 500);
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.printing = false;
      }
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}
.info-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.info-row > span:last-child {
  text-align: right;
  max-width: 60%;
}
</style>
